import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@/shared/i18n';

import styles from './Toast.module.css';
import { ToastContext, type ToastOptions } from './toast-context';

/** How long a toast stays when nothing else happens. */
export const TOAST_MS = 7000;

interface Item extends ToastOptions {
  id: number;
  pending: boolean;
  error: string | null;
}

/**
 * Temporary, non-blocking notifications with one optional action. The toast removes itself
 * after `durationMs`; while its action runs it shows a busy state, and a failure replaces the
 * message with the error (it then stays for the usual time so it can be read).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const [items, setItems] = useState<Item[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    window.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const arm = useCallback(
    (id: number, ms: number) => {
      window.clearTimeout(timers.current.get(id));
      timers.current.set(
        id,
        window.setTimeout(() => {
          dismiss(id);
        }, ms),
      );
    },
    [dismiss],
  );

  const show = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      setItems((current) => [
        ...current.slice(-2),
        { ...options, id, pending: false, error: null },
      ]);
      arm(id, options.durationMs ?? TOAST_MS);
    },
    [arm],
  );

  useEffect(() => {
    const active = timers.current;
    return () => {
      active.forEach((timer) => {
        window.clearTimeout(timer);
      });
    };
  }, []);

  const run = (item: Item) => {
    if (item.pending || !item.onAction) return;
    window.clearTimeout(timers.current.get(item.id)); // no auto-dismiss while it is running
    setItems((current) => current.map((i) => (i.id === item.id ? { ...i, pending: true } : i)));
    item.onAction().then(
      () => {
        dismiss(item.id);
      },
      (cause: unknown) => {
        setItems((current) =>
          current.map((i) =>
            i.id === item.id
              ? { ...i, pending: false, error: getErrorMessage(cause), onAction: undefined }
              : i,
          ),
        );
        arm(item.id, item.durationMs ?? TOAST_MS);
      },
    );
  };

  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext value={api}>
      {children}
      <div className={styles.host} role="region" aria-label={t('toast.region')}>
        {items.map((item) => (
          <div
            key={item.id}
            className={`${styles.toast} ${item.error ? styles.error : ''}`}
            role={item.error ? 'alert' : 'status'}
            aria-busy={item.pending}
            data-testid="toast"
          >
            <span className={styles.message}>{item.error ?? item.message}</span>
            {item.actionLabel && item.onAction && (
              <button
                type="button"
                className={styles.action}
                disabled={item.pending}
                onClick={() => {
                  run(item);
                }}
              >
                {item.pending ? t('toast.working') : item.actionLabel}
              </button>
            )}
            <button
              type="button"
              className={styles.close}
              disabled={item.pending}
              aria-label={t('toast.dismiss')}
              onClick={() => {
                dismiss(item.id);
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}
